// deps
import React, { useCallback, memo, ReactNode, useMemo } from "react";

// internal deps
import { isEmpty } from "@swiggy-private/js-utils";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";

// styles
import {
    AmountContainer,
    ButtonContainer,
    DiscountContainer,
    MostPopularAmountContainer,
} from "./styles";
import { GIFT_CARD_SELECTION_TEST_IDS } from "../../constants";
import { MOST_POPULAR_TAG, OTHER_AMOUNT } from "../../constants/gift-card";

// constants

interface AmountBoxProps {
    value: string;
    setAmountSelection: (val: string) => void;
    discount?: number;
    discountMinimumValue?: number;
    emojiNode: ReactNode;
    showMostPopularTag: boolean;
    isAmountSelected: boolean;
    text: string;
    isCustomSelected: boolean;
}

const AmountBox = ({
    value,
    setAmountSelection,
    discount,
    discountMinimumValue,
    emojiNode,
    isAmountSelected,
    text,
    isCustomSelected,
    showMostPopularTag,
}: AmountBoxProps) => {
    const selected = useMemo(() => {
        return value == String(OTHER_AMOUNT) ? isCustomSelected : isAmountSelected;
    }, [isAmountSelected, isCustomSelected, value]);

    const isDiscountApplicable = useMemo(() => {
        return discountMinimumValue && Number(value) >= discountMinimumValue;
    }, [discountMinimumValue, value]);

    const handleClick = useCallback(() => {
        if (isAmountSelected) {
            setAmountSelection("");
        } else {
            setAmountSelection(value);
        }
    }, [isAmountSelected, setAmountSelection, value]);

    const showDiscountedTag = useMemo(
        () => !isEmpty(discount) && isDiscountApplicable,
        [discount, isDiscountApplicable]
    );

    return (
        <ButtonContainer
            onClick={handleClick}
            selected={selected}
            data-testid={GIFT_CARD_SELECTION_TEST_IDS.AMOUNT_BOX_CONTAINER}
        >
            {showDiscountedTag && (
                <DiscountContainer>
                    <Text
                        variant="V2.caption.neutral.bold"
                        color="background_Primary"
                    >{`${discount}% OFF`}</Text>
                </DiscountContainer>
            )}
            {!showDiscountedTag && showMostPopularTag && (
                <MostPopularAmountContainer>
                    <Text variant="V2.caption.neutral.bold" color="background_Primary">
                        {MOST_POPULAR_TAG}
                    </Text>
                </MostPopularAmountContainer>
            )}
            {emojiNode}
            <AmountContainer
                variant={selected ? "V2.body2.neutral.bold" : "V2.body2.neutral.medium"}
                color={selected ? "primary" : "text_High_Emphasis"}
            >
                {text}
            </AmountContainer>
        </ButtonContainer>
    );
};

export default memo(AmountBox);
